import { dispatchAction } from '@ardoq/rxbeach';
import { updateViewSettings } from '@ardoq/view-settings';
import { addToPresentation } from 'viewSettings/exportHandlers';
import {
  getReferenceTypeDropdownOptions,
  MenuMetaOptions,
} from '@ardoq/settings-bar';
import {
  type DropdownConfig,
  type SettingsConfig,
  SettingsType,
  type ToggleConfig,
  viewSettingsConsts,
} from '@ardoq/view-settings';
import {
  DropdownOption,
  DropdownOptionType,
  Notifier,
} from '@ardoq/dropdown-menu';
import { IconName } from '@ardoq/icons';
import { HeaderModel, HeaderType, TableViewSettings } from './types';
import { isTruthy } from '@ardoq/common-helpers';
import { ViewIds } from '@ardoq/api-types';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import { partition } from 'lodash';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { t, type I18nLocale } from '@ardoq/i18n';

const UNUSED_RECORD = {};

const SHOW_ALL = Symbol('SHOW_ALL');
const HIDE_ALL = Symbol('HIDE_ALL');

type GetRightMenuArgs = {
  viewState: TableViewSettings;
  knowledgeBaseLink: string;
  viewId: ViewIds;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  isEmptyView: boolean;
  isLegendPossible: boolean;
  i18nLocale: I18nLocale;
  userHasWriteAccess: boolean;
};
export const getRightMenu = ({
  viewState,
  knowledgeBaseLink,
  viewId,
  onViewSettingsUpdate: onUpdateViewSettings,
  isEmptyView,
  isLegendPossible,
  i18nLocale,
  userHasWriteAccess,
}: GetRightMenuArgs): SettingsConfig[] => {
  return [
    knowledgeBaseLink
      ? {
          id: 'knowledgeBaseLink',
          label: t(i18nLocale, 'settingsBar:knowledgeBase', 'Knowledge base'),
          iconName: IconName.HELP,
          type: SettingsType.BUTTON,
          onClick: () => window.open(knowledgeBaseLink, '_blank'),
          isActive: false,
        }
      : null,
    {
      id: viewSettingsConsts.IS_LEGEND_ACTIVE,
      label: t(i18nLocale, 'settingsBar:toggleLegend', 'Toggle legend'),
      iconName: IconName.INFO,
      isActive: viewState[viewSettingsConsts.IS_LEGEND_ACTIVE],
      isDisabled: !isLegendPossible || isEmptyView,
      popoverText: !isLegendPossible
        ? t(
            i18nLocale,
            'settingsBar:tableView.legendImpossiblePopover',
            'Legend is available when a conditional format is added in Perspectives and at least 1 column displays components'
          )
        : undefined,
      persistent: false,
      type: SettingsType.TOGGLE,
      onViewSettingsUpdate: onUpdateViewSettings,
    },
    {
      id: viewSettingsConsts.FULLSCREEN_KEY,
      label: viewState[viewSettingsConsts.FULLSCREEN_KEY]
        ? t(i18nLocale, 'settingsBar:leaveFullscreen', 'Leave Fullscreen')
        : t(i18nLocale, 'settingsBar:fullscreen', 'Fullscreen'),
      isActive: viewState[viewSettingsConsts.FULLSCREEN_KEY],
      iconName: viewState[viewSettingsConsts.FULLSCREEN_KEY]
        ? IconName.FULLSCREEN_EXIT
        : IconName.FULLSCREEN,
      type: SettingsType.TOGGLE,
      persistent: false,
      onViewSettingsUpdate: onUpdateViewSettings,
    },
    {
      id: 'export',
      label: t(i18nLocale, 'settingsBar:export', 'Export'),
      type: SettingsType.EXPORT,
      addToPresentation: userHasWriteAccess
        ? () => addToPresentation(viewId, false)
        : undefined,
      // Excluded, because it may cause browser crash. https://ardoqcom.atlassian.net/browse/ARD-16359
      // exportToPng: pngExporter,
      isDisabled: isEmptyView,
    },
  ].filter(isTruthy);
};

type ToggleFieldArgs = {
  visibleColumnsSet: Set<string>;
  headers: HeaderModel[];
  key: string | symbol;
};

const composeNewViewSettings = ({
  visibleColumnsSet,
  headers,
  key,
}: ToggleFieldArgs) => {
  const allColumns = headers.map(column => column.key);

  // hideAllColumns is needed to answer the question: are there no visible columns
  // because we have a new user, or is it so because all columns schould be hidden?
  let hideAllColumns = false;

  switch (key) {
    case SHOW_ALL:
      allColumns.forEach(col => visibleColumnsSet.add(col));
      break;
    case HIDE_ALL:
      allColumns.forEach(col => visibleColumnsSet.delete(col));
      hideAllColumns = true;
      break;
    default:
      if (typeof key === 'string') {
        if (visibleColumnsSet.has(key)) {
          visibleColumnsSet.delete(key);
        } else {
          visibleColumnsSet.add(key);
        }
      }
      hideAllColumns = allColumns.every(
        columnKey => !visibleColumnsSet.has(columnKey)
      );
  }

  return {
    visibleColumns: Array.from(visibleColumnsSet),
    hideAllColumns,
  };
};

type GetOptionClickHandlerArgs = ToggleFieldArgs & {
  viewId: ViewIds;
};
const getOptionClickHandler =
  ({ viewId, headers, visibleColumnsSet, key }: GetOptionClickHandlerArgs) =>
  () =>
    dispatchAction(
      updateViewSettings({
        viewId,
        settings: composeNewViewSettings({
          visibleColumnsSet,
          headers,
          key,
        }),
        persistent: true,
      })
    );

type GetDropDownOptionsArgs = {
  viewId: ViewIds;
  showAll: boolean;
  hideAllColumns: boolean;
  defaultHeaders: HeaderModel[];
  referenceTypeHeaders: HeaderModel[];
  fieldHeaders: HeaderModel[];
  visibleColumnsSet: Set<string>;
  i18nLocale: I18nLocale;
};
const getDropdownOptions = ({
  viewId,
  showAll,
  hideAllColumns,
  defaultHeaders,
  referenceTypeHeaders,
  fieldHeaders,
  visibleColumnsSet,
  i18nLocale,
}: GetDropDownOptionsArgs) => {
  const allHeaders = [
    ...defaultHeaders,
    ...referenceTypeHeaders,
    ...fieldHeaders,
  ];
  const [incomingReferenceHeaders, outgoingReferenceHeaders] = partition(
    referenceTypeHeaders,
    ({ type }) => type === HeaderType.SOURCE
  );
  const referenceTypeOptions = getReferenceTypeDropdownOptions({
    viewId,
    availableIncomingRefTypes: incomingReferenceHeaders,
    availableOutgoingRefTypes: outgoingReferenceHeaders,
    viewState: UNUSED_RECORD,
    metaOptions: MenuMetaOptions.NO_OPTIONS,
    isActive: ({ key }) => Boolean(key && visibleColumnsSet.has(key)),
    isParentRelationAsReference: false,
    onClick: ({ key }) =>
      getOptionClickHandler({
        viewId,
        headers: allHeaders,
        visibleColumnsSet,
        key,
      })(),
    formatValue: ({ label }) => label,
    onViewSettingsUpdate,
    i18nLocale,
  });

  const [defaultHeaderOptions, fieldHeaderOptions] = [
    defaultHeaders,
    fieldHeaders,
  ].map(headers =>
    headers.map(({ key, label }) => {
      // show all colums as active if there are no visible columns but we should use them
      const isActive = visibleColumnsSet?.size
        ? visibleColumnsSet.has(key)
        : !hideAllColumns;

      const option: DropdownOption = {
        label,
        isActive,
        onClick: getOptionClickHandler({
          viewId,
          headers: allHeaders,
          visibleColumnsSet,
          key,
        }),
        type: DropdownOptionType.OPTION,
        truncateLabel: true,
      };

      return option;
    })
  );
  return [
    {
      label: t(i18nLocale, 'settingsBar:showAll', 'Show all'),
      name: 'show_all',
      isActive: showAll,
      onClick: getOptionClickHandler({
        viewId,
        headers: allHeaders,
        visibleColumnsSet,
        key: SHOW_ALL,
      }),
      type: DropdownOptionType.OPTION,
    },
    {
      label: t(i18nLocale, 'settingsBar:tableView.hideAll', 'Hide all'),
      name: 'hide_all',
      isActive: hideAllColumns,
      onClick: getOptionClickHandler({
        viewId,
        headers: allHeaders,
        visibleColumnsSet,
        key: HIDE_ALL,
      }),
      type: DropdownOptionType.OPTION,
    },
    {
      type: DropdownOptionType.DIVIDER,
    },
    defaultHeaderOptions.length && {
      type: DropdownOptionType.HEADER,
      label: t(i18nLocale, 'settingsBar:tableView.default', 'Default'),
    },
    ...defaultHeaderOptions,
    ...referenceTypeOptions,
    fieldHeaderOptions.length && {
      type: DropdownOptionType.HEADER,
      label: t(i18nLocale, 'settingsBar:tableView.fields', 'Fields'),
    },
    ...fieldHeaderOptions,
  ].filter(isTruthy);
};

type GetLeftMenuArgs = {
  viewId: ViewIds.REFERENCETABLE | ViewIds.TABLEVIEW;
  viewState: TableViewSettings;
  defaultHeaders: HeaderModel[];
  referenceTypeHeaders: HeaderModel[];
  fieldHeaders: HeaderModel[];
  onUpdateViewSettings: OnViewSettingsUpdate;
  openShowColumnsViewSetting: Notifier;
  i18nLocale: I18nLocale;
};
export const getLeftMenu = ({
  viewId,
  viewState: { expandDescription, visibleColumns, hideAllColumns },
  defaultHeaders,
  referenceTypeHeaders,
  fieldHeaders,
  onUpdateViewSettings,
  openShowColumnsViewSetting,
  i18nLocale,
}: GetLeftMenuArgs): SettingsConfig[] => {
  const allHeaders = [
    ...defaultHeaders,
    ...referenceTypeHeaders,
    ...fieldHeaders,
  ];
  const allColumns = allHeaders.map(h => h.key);

  const visibleColumnsSet = new Set(visibleColumns || []);

  const showAll = visibleColumnsSet.size
    ? allColumns.every(columnKey => visibleColumnsSet.has(columnKey))
    : !hideAllColumns; // if there are no visible cols specified and we should not hide all columns - show them all

  const toggleColumnsDropdown: DropdownConfig = {
    id: 'toggleColumns',
    label: t(
      i18nLocale,
      'settingsBar:tableView.toggleColumns',
      'Toggle Columns'
    ),
    iconName: IconName.SPLIT_SCREEN,
    options: getDropdownOptions({
      viewId,
      showAll,
      hideAllColumns,
      defaultHeaders,
      referenceTypeHeaders,
      fieldHeaders,
      visibleColumnsSet,
      i18nLocale,
    }),
    type: SettingsType.DROPDOWN,
    isDisabled: false,
    isKeepOpen: true,
    requestOpen: openShowColumnsViewSetting,
  };

  const expandDescriptionButton: ToggleConfig = {
    id: 'expandDescription',
    label: expandDescription
      ? t(
          i18nLocale,
          'settingsBar:tableView.collapseDescriptions',
          'Collapse Descriptions'
        )
      : t(
          i18nLocale,
          'settingsBar:tableView.expandDescriptions',
          'Expand Descriptions'
        ),
    iconName: expandDescription ? IconName.UNFOLD_LESS : IconName.UNFOLD_MORE,
    type: SettingsType.TOGGLE,
    isActive: expandDescription,
    isDisabled: false,
    onViewSettingsUpdate: onUpdateViewSettings,
  };

  return [toggleColumnsDropdown, expandDescriptionButton];
};
